import { createParser } from 'eventsource-parser'
import type {
  DeepAAgentRunState,
  DeepAAgentStartedRun,
  DeepAAgentStartRunBody,
} from '@/pocs/chat/types'

const DEEPA_CLIENT = 'deepbox'

const RUNS_URL = `${import.meta.env.VITE_DEEPA_API_BASE_URL}/api/agent/v1/runs`

interface FollowRunCallbacks {
  signal?: AbortSignal
  onStatus?: (state: Partial<DeepAAgentRunState>) => void
  onReply?: (reply: string) => void
  onDone?: (state: DeepAAgentRunState) => void
}

export class DeepAAgentApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail?: string,
  ) {
    super(detail || `HTTP ${status}`)
  }
}

// only JSON or plain-text bodies carry a readable reason; gateway errors come as HTML
async function readErrorDetail(response: Response) {
  const contentType = response.headers.get('content-type') ?? ''
  try {
    if (contentType.includes('json')) {
      const body = (await response.json()) as Record<string, unknown>
      const message = body.message ?? body.detail ?? body.error ?? body.title
      return typeof message === 'string' ? message : undefined
    }
    if (contentType.includes('text/plain')) {
      return (await response.text()) || undefined
    }
  } catch {
    return undefined
  }
  return undefined
}

async function toResponseError(response: Response) {
  return new DeepAAgentApiError(
    response.status,
    await readErrorDetail(response),
  )
}

export function useDeepAAgentApi(
  companyIdSource: MaybeRefOrGetter<string | undefined>,
) {
  const { locale } = useI18n()

  function getHeaders() {
    const headers: Record<string, string> = {
      'X-DeepA-Client': DEEPA_CLIENT,
      'Accept-Language': locale.value,
    }
    const companyId = toValue(companyIdSource)
    if (companyId) {
      headers.companyId = companyId
    }
    return headers
  }

  async function startRun(
    body: DeepAAgentStartRunBody,
  ): Promise<DeepAAgentStartedRun> {
    const response = await fetch(RUNS_URL, {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      throw await toResponseError(response)
    }
    return response.json()
  }

  // read with fetch because EventSource cannot send custom headers
  async function followRun(
    jobId: string,
    { signal, onStatus, onReply, onDone }: FollowRunCallbacks,
  ) {
    const response = await fetch(`${RUNS_URL}/${jobId}/events`, {
      headers: {
        ...getHeaders(),
        Accept: 'text/event-stream',
      },
      signal,
    })
    if (!response.ok || !response.body) {
      throw await toResponseError(response)
    }

    let isDone = false
    const parser = createParser({
      onEvent({ event, data }) {
        if (!data) return
        switch (event) {
          case 'status':
            onStatus?.(JSON.parse(data) as Partial<DeepAAgentRunState>)
            break
          case 'reply':
            onReply?.((JSON.parse(data) as { reply: string }).reply)
            break
          case 'done':
            isDone = true
            onDone?.(JSON.parse(data) as DeepAAgentRunState)
            break
        }
      },
    })

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader()
    try {
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        parser.feed(value)
      }
    } finally {
      reader.cancel().catch(() => undefined)
    }
    return { isDone }
  }

  // best effort: callers never wait for the abort to be confirmed
  async function abortRun(jobId: string) {
    try {
      const response = await fetch(`${RUNS_URL}/${jobId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      })
      return response.ok
    } catch {
      return false
    }
  }

  return { startRun, followRun, abortRun }
}
